import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { SettingsEditState } from '@ops/services';
import {
  CompanyService,
  LocalStorageService,
  NameFilter,
  OpsSubscriptionModalService,
  PricingPlanMapped,
  StripeService,
  SubscriptionStatus,
} from '@ops/shared-types';
import { SpinnerComponent } from 'legacy/shared/components/src/lib/spinner/spinner.component';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { OpsSubscriptionCardComponent } from '../ops-subscription-card/ops-subscription-card.component';
import { OpsSubscriptionMessageModal } from '../ops-subscription-message-modal/ops-subscription-message-modal.component';
import { OpsAnnualMonthlySwitchComponent } from '../ops-annual-monthly-switch/ops-annual-monthly-switch.component';
import { OpsCurrencySwitchComponent } from '../ops-currency-switch/ops-currency-switch.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-welcome-stripe',
  standalone: true,
  imports: [
    CommonModule,
    OpsSubscriptionCardComponent,
    OpsSubscriptionMessageModal,
    OpsAnnualMonthlySwitchComponent,
    OpsCurrencySwitchComponent,
    SpinnerComponent,
    TranslateModule,
  ],
  templateUrl: './ops-welcome-stripe.component.html',
  styleUrl: './ops-welcome-stripe.component.scss',
})
export class OpsWelcomeStripe implements OnInit, OnDestroy {
  editState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getEditState
  );

  saveState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getSaveState
  );

  selectedSubscriptionData: any;
  // selectedPlanId = 'prod_RuFNKXTXGxa7Ij';
  subscriptionPlans?: Array<PricingPlanMapped>;

  initialLoading: boolean = false;

  subs: Array<Subscription> = [];

  allSubscriptions: Array<any> = [];

  selectedInterval: string = 'annual';
  selectedCurrency: string = 'USD';

  allUserCompanies: NameFilter[];

  showCreateNewOrganisation = false;
  subscriptionId;

  confirmSubscription: Subscription;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private stripeService: StripeService,
    private localStorageService: LocalStorageService,
    private companyService: CompanyService,
    private modalService: OpsSubscriptionModalService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {

    

    const sProd = this.stripeService
      .getStripeProducts()
      .subscribe((stripeProducts: PricingPlanMapped[]) => {
        this.subscriptionPlans = [...stripeProducts];
      });

    

    const allUserCompanies$ = this.getAllUserCompanies();
    const allUserSubscriptions$ = this.getAllUserSubscriptions();

    forkJoin([allUserCompanies$, allUserSubscriptions$]).subscribe({
      next: ([userCompanies, userSubscriptions]: any) => {
        // console.log(
        //   'Companies/Subscriptions',
        //   userCompanies.length,
        //   userSubscriptions.length
        // );

        if (userSubscriptions.length && !userCompanies.length) {
          this.router.navigate(['/welcome']);
          return;
        }

        if (userSubscriptions.length && userCompanies.length) {
          this.router.navigate(['/settings/my-subscriptions']);
          return;
        }

        this.allUserCompanies = userCompanies;
        const usersSubs = userSubscriptions.map((userSubs) => {
          return {
            ...userSubs,
            companyName: userCompanies?.find(
              (userComp) => userComp._id === userSubs.company
            )?.name,
          };
        });

        const connectedSubs = usersSubs.filter(
          (withCompany: any) => !!withCompany?.company
        );
        const emptySubs = usersSubs.filter(
          (withoutCompany: any) => !withoutCompany?.company
        );

        this.allSubscriptions = [...connectedSubs, ...emptySubs].sort((a, b) =>
          a?.companyName?.localeCompare(b?.companyName)
        );

        this.initialLoading = false;
      },
      error: (error) => {
        console.error('An error occurred:', error);
      },
      complete: () => {
        // console.log('All updates completed.');
        const statusValue = this.route.snapshot.queryParamMap.get('status');
        if (statusValue) {
          if (statusValue === 'successfull' || statusValue === 'success') {
            this.showSuccessModal();
          } else {
            this.showErrorModal();
          }
        }
      },
    });

    this.subs.push(sProd);

    // this.subs.push(companiesSub);

    // this.stripeUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://checkout.stripe.com/c/pay/cs_test_a1xPDR0h6UsafmdWy0YAzFlQESGPvLMFZ6YaBADLCbIyTN9NZZAsvGdwc2#fidkdWxOYHwnPyd1blpxYHZxWjA0VF1zfXZASVFBRGYxdEZCMWlJPFFjYFVLaW5xTW0xXWZIM219dnRPSzBEY2lHYUxJSDI0U0FGQUtmQTF2bVVpZExxNHB8cTNGT0dtZ0hhZlFmX01EQk1DNTU1PEdXUDU0RCcpJ2N3amhWYHdzYHcnP3F3cGApJ2lkfGpwcVF8dWAnPyd2bGtiaWBabHFgaCcpJ2BrZGdpYFVpZGZgbWppYWB3dic%2FcXdwYHgl');
  }

  onSubscriptionSelect(subscriptionItem) {
    // console.log('subscriptionItem', subscriptionItem);
    if (subscriptionItem) {
      this.selectedSubscriptionData = subscriptionItem;
    }
  }

  getAllUserCompanies() {
    return this.companyService.getCompanyFilters();
  }

  getAllUserSubscriptions() {
    return this.stripeService.getAllUserSubscriptions(true);
  }

  ngOnDestroy(): void {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.subs.forEach((s) => s.unsubscribe());
  }

  get showingMonthly(): boolean {
    return this.selectedInterval === 'monthly';
  }

  onIntervalChange(interval: string) {
    this.selectedInterval = interval;
  }

  onCurrencyChange(currency: string) {
    this.selectedCurrency = currency;
    // console.log('selectedCurrency', this.selectedCurrency);
  }

  onSelectPlan(newPlan: string) {
    this.stripeService.postStripePlainId(newPlan).subscribe((resp: any) => {
      const respJson = JSON.parse(resp);
      if (respJson?.url) {
        window.open(respJson.url, '_self');
      }
    });

    // this.isChangingPlan = false;
    // console.log("selectedPlanId", newPlan);
    // this.selectedPlanId = newPlan;
  }

  showSuccessModal() {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    const modalText = this.translateService.instant('OPS_SUBSCRIPTION_SETTINGS.SUBSCRIPTION_SUCCESSFUL');
    this.confirmSubscription = this.modalService
      .openModal({
        statusType: SubscriptionStatus.SUCCESS,
        modalText: modalText,
      })
      .subscribe((confirmed) => {
        if (confirmed !== null) {
          // Handle confirmation (only relevant for confirmation dialogs)
          // console.log('User confirmed:', confirmed);
        }
      });
  }

  showErrorModal() {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    const modalText = this.translateService.instant('OPS_SUBSCRIPTION_SETTINGS.SUBSCRIPTION_ERROR');
    
    this.modalService.openModal({
      statusType: SubscriptionStatus.ERROR,
      modalText: modalText,
    });
  }

  showConfirmationModal() {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    const modalText = this.translateService.instant('OPS_SUBSCRIPTION_SETTINGS.CANCEL_SUBSCRIPTION_CONFIRMATION');
    const modalTitle = this.translateService.instant('OPS_SUBSCRIPTION_SETTINGS.CONFIRM_CANCELLATION');
    this.confirmSubscription = this.modalService
      .openModal({
        statusType: SubscriptionStatus.CONFIRMATION,
        modalText: modalText,
        modalTitle: modalTitle,
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          // User confirmed
          // console.log('User confirmed cancellation');
        } else {
          // User declined
          // console.log('User cancelled the action');
        }
      });
  }
}
